import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { calculateCost, WAR_BRACKETS } from '../utils/gameRules';
import { Inventory, PieceType, Profile } from '../types';
import { SupabaseService } from '../services/supabaseService';
import { PieceSelector } from '../components/Drafting/PieceSelector';
import { StarterPack } from '../components/Drafting/StarterPack';
import { Shield, Save, X, Lock, ShoppingBag } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import { supabase } from '../services/supabaseClient';
import AbdicationModal from '../components/AbdicationModal';
import { WagerControl } from '../components/Drafting/WagerControl';

const INITIAL_RESOURCES = 200; // Default to Tier 1 Max

const Drafting: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { error } = useAlert();

  // Mode State
  const targetWarId = searchParams.get('warId');
  const targetRole = searchParams.get('role') as 'host' | 'guest' | null;
  const isJoinMode = !!targetWarId;

  const [inventory, setInventory] = useState<Inventory>({ p: 0, n: 0, b: 0, r: 0, q: 0, k: 1 });
  const [resources, setResources] = useState(INITIAL_RESOURCES); // This is now Deployment Capacity
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [opponentType, setOpponentType] = useState<'player' | 'ai'>('player');
  const [isRanked, setIsRanked] = useState(true);
  const [selectedBracketId, setSelectedBracketId] = useState<string>('SKIRMISH');
  const [userProfile, setUserProfile] = useState<Profile | null>(null);

  // Abdication / Ranked State
  const [showAbdication, setShowAbdication] = useState(false);
  const [abdicationReason, setAbdicationReason] = useState<'bankruptcy' | 'revoked' | 'ineligible'>('ineligible');

  // Wagering
  const [wagerAmount, setWagerAmount] = useState<number>(0);

  // Load Existing War if Join Mode
  useEffect(() => {
    const initJoinMode = async () => {
      if (!targetWarId) return;

      const war = await SupabaseService.getWarById(targetWarId);
      if (!war) {
        error("Campaign not found.");
        navigate('/campaigns');
        return;
      }

      // Lock Settings to Match War
      setIsRanked(!!war.is_ranked);
      setOpponentType(war.settings?.opponent_type === 'ai' ? 'ai' : 'player');

      if (war.bracket_id && WAR_BRACKETS[war.bracket_id]) {
        setSelectedBracketId(war.bracket_id);
        setResources(WAR_BRACKETS[war.bracket_id].maxCost);
      }
    };

    if (isJoinMode) initJoinMode();
  }, [targetWarId, targetRole, navigate]);

  const handleToggleRanked = async () => {
    if (isJoinMode) return;

    // Switching TO Unranked (Premium Only)
    if (isRanked) {
      if (!userProfile?.is_premium) {
        error("Restricted Access: Unranked Skirmishes are reserved for Royal Subscribers.");
        return;
      }
      setIsRanked(false);
      return;
    }

    // Switching TO Ranked (Free)
    if (!userProfile) return;

    // 1. Eligibility Check
    const { eligible, reason } = await SupabaseService.checkRankedEligibility();
    if (!eligible) {
      if (reason?.includes("bankrupt")) setAbdicationReason('bankruptcy');
      else if (reason?.includes("revoked")) setAbdicationReason('revoked');
      else setAbdicationReason('ineligible');

      if (reason?.includes("bankrupt") || reason?.includes("revoked")) {
        setShowAbdication(true);
      } else {
        error(reason || "Ranked Unavailable");
      }
      return;
    }

    // 2. Active Campaign Limit Check Removed (Now tied to King Inventory)


    setIsRanked(true);
  };

  // Save Modal State
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveDesc, setSaveDesc] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      const profile = await SupabaseService.getProfile();
      setUserProfile(profile);
    };
    loadProfile();
  }, [isSaveModalOpen]);

  // Recalculate resources whenever inventory changes
  useEffect(() => {
    const used = calculateCost(inventory);
    // Resource limit is based on Bracket Max
    const bracket = WAR_BRACKETS[selectedBracketId];
    if (bracket) {
      // setResources shows REMAINING budget from the Bracket Max
      setResources(bracket.maxCost - used);
    }
  }, [inventory, selectedBracketId]);

  const handlePieceChange = (type: PieceType, delta: number) => {
    const newInventory = { ...inventory };
    newInventory[type] = Math.max(0, newInventory[type] + delta);

    // 1. Check constraints (Generic)
    if (type === 'k' || type === 'q') {
      if (newInventory[type] > 1) return; // Hard limit 1 per battle
    }

    // 2. Check Cost (Deployment Limit)
    const potentialCost = calculateCost(newInventory);
    const bracket = WAR_BRACKETS[selectedBracketId];
    if (bracket.maxCost - potentialCost < 0) return;

    // 3. Check Standing Army Reserves (Only for Ranked)
    if (isRanked && userProfile && delta > 0) {
      // If we are adding, check if we have enough
      {

        const reserve = userProfile.inventory[type] || 0;
        if (newInventory[type] > reserve) {
          error(`Insufficient Reserves! You only have ${reserve} ${type.toUpperCase()} in your Barracks.`);
          return;
        }
      }
    }

    setInventory(newInventory);
  };

  const handleStarterPack = (pack: Inventory) => {
    const cost = calculateCost(pack);
    const bracket = WAR_BRACKETS[selectedBracketId];

    // Check Budget
    if (bracket.maxCost - cost < 0) {
      error("Fielding Limit Exceeded for this bracket!");
      return;
    }

    // Check Reserves (Only for Ranked)
    if (isRanked && userProfile) {
      const missingParts: string[] = [];

      (Object.keys(pack) as Array<keyof Inventory>).forEach(k => {
        {

          const required = pack[k];
          const owned = userProfile.inventory[k] || 0;

          if (required > owned) {
            // e.g. "Need 60 P (Have 50)"
            const pieceName = k.toUpperCase();
            missingParts.push(`${required - owned} more ${pieceName}`);
          }
        }
      });

      if (missingParts.length > 0) {
        error(`Insufficient Reserves for this formation! Missing: ${missingParts.join(', ')}.`);
        return;
      }
    }

    setInventory(pack);
  };

  const handleConfirm = async () => {
    if (inventory.k < 1 || inventory.q < 1) {
      error("You must have a King and a Queen!");
      return;
    }

    if (inventory.p < 8) {
      error("By Royal Decree: You must field at least 8 Conscripts (Pawns) to form a regiment.");
      return;
    }

    const cost = calculateCost(inventory);
    const bracket = WAR_BRACKETS[selectedBracketId];

    // Validate Gold for Ranked - REMOVED
    // Previous check for gold >= cost is now skipped as wars are free to enter.

    // Check Active Ranked Limit - REMOVED (Tied to Kings)


    // JOIN MODE EXECUTION
    if (isJoinMode && targetWarId) {
      try {
        if (targetRole === 'guest') {
          // Sequential "Claim then Update" flow to respect granular RLS policies.
          // 1. Ensure we are the opponent (Claim slot if null, or verify ownership)
          await SupabaseService.joinLobby(targetWarId);

          // 2. Commit Deployment (Update Inventory)
          // This succeeds because Step 1 established the 'opponent_id' permission.
          await SupabaseService.updateEnemyInventory(targetWarId, inventory);
        } else {
          await SupabaseService.updateInventory(targetWarId, inventory);
        }
        navigate(`/war-room/${targetWarId}`);
        return;
      } catch (err: any) {
        console.error("Join/Deploy Failed:", err);
        error(err.message || "Failed to deploy. Server rejected the formation.");
        return;
      }
    }

    // Validate Wager
    if (isRanked && userProfile && wagerAmount > 0) {
      if (userProfile.gold < wagerAmount) {
        error(`Insufficient Funds for Wager! You have ${userProfile.gold} Gold.`);
        return;
      }
    }

    const war = await SupabaseService.createWar(inventory, cost, difficulty, isRanked, bracket, opponentType, wagerAmount);
    if (war) {
      navigate(`/war-room/${war.id}`);
    } else {
      error("Failed to declare war. Please try again.");
    }
  };

  const handleSaveTemplate = async () => {
    if (!saveTitle.trim()) {
      error("Please provide a title for your template.");
      return;
    }

    try {
      await SupabaseService.saveTemplate({
        name: saveTitle,
        description: saveDesc || 'No description provided.',
        inventory: { ...inventory }
      });

      // Close and Reset
      setIsSaveModalOpen(false);
      setSaveTitle('');
      setSaveDesc('');
    } catch (err: any) {
      console.error("Failed to save template:", err);
      error(err.message || "Failed to save template. Please try again.");
    }
  };

  const currentArmySize = Object.values(inventory).reduce((a, b) => a + b, 0);

  return (
    <div className="p-4 max-w-4xl mx-auto pb-20 relative">
      <AbdicationModal
        isOpen={showAbdication}
        onClose={() => setShowAbdication(false)}
        reason={abdicationReason}
      />
      {/* SAVE MODAL */}
      {isSaveModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-xl border border-zinc-700 bg-zinc-900 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-tudor-gold">Save Requisition</h2>
              <button onClick={() => setIsSaveModalOpen(false)} className="text-zinc-500 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Config Title</label>
                <input
                  type="text"
                  value={saveTitle}
                  onChange={(e) => setSaveTitle(e.target.value)}
                  placeholder="e.g., Heavy Cavalry Flank"
                  className="w-full bg-black border border-zinc-700 p-3 text-white focus:border-tudor-gold focus:outline-none rounded"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Description (Optional)</label>
                <textarea
                  value={saveDesc}
                  onChange={(e) => setSaveDesc(e.target.value)}
                  placeholder="Tactical notes..."
                  className="w-full bg-black border border-zinc-700 p-3 text-white focus:border-tudor-gold focus:outline-none rounded h-24 resize-none"
                />
              </div>

              <div className="flex items-center justify-between bg-zinc-800/50 p-4 rounded border border-zinc-700/50">
                <span className="text-xs uppercase text-zinc-500">Auto-Generated Manifest</span>
                <span className="text-tudor-gold font-mono">{currentArmySize} Units</span>
              </div>

              <button
                onClick={handleSaveTemplate}
                className="w-full bg-tudor-gold text-black font-bold py-3 rounded hover:bg-yellow-600 transition-colors uppercase tracking-widest mt-4"
              >
                Commit to Records
              </button>
            </div>
          </div>
        </div>
      )}
      <header className="mb-8 border-b border-tudor-gray pb-4 flex flex-col md:flex-row md:justify-between md:items-end gap-4">
        <div>
          {isJoinMode ? (
            <div className="flex items-center gap-2 mb-2 bg-tudor-gold/10 px-3 py-1 rounded w-fit border border-tudor-gold/30">
              <Lock size={12} className="text-tudor-gold" />
              <span className="text-xs text-tudor-gold uppercase tracking-wider">Locked to {targetRole === 'guest' ? 'Opponent' : 'Host'} Draft</span>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-gray-400">Reinforce Regiment</span>
            <span className={`font-mono px-2 py-0.5 rounded text-black font-bold text-sm ${resources < 0 ? 'bg-red-500' : 'bg-tudor-gold'}`}>
              Capacity: {resources}
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">
            Bracket: <span className="text-tudor-gold">{WAR_BRACKETS[selectedBracketId].name}</span>
          </div>
        </div>

        <div className="flex flex-row md:flex-col gap-4 md:gap-2 items-center md:items-end w-full md:w-auto justify-between md:justify-end">
          <button onClick={() => navigate('/barracks')} className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded text-xs uppercase tracking-wider text-tudor-gold border border-tudor-gold/30 transition-colors">
            <ShoppingBag size={14} /> Buy Troops
          </button>
          {userProfile && (
            <div className="font-mono text-xs tracking-widest text-zinc-500 text-right">
              Reserves: <span className="text-white">{(Object.values(userProfile.inventory || {}).reduce((a: number, b: number) => a + b, 0))} Units</span>
            </div>
          )}
        </div>
      </header>

      {/* WAR SETUP */}
      <div className={`mb-8 p-4 bg-zinc-900/50 border border-zinc-700/50 rounded-lg ${isJoinMode ? 'opacity-60 grayscale pointer-events-none' : ''}`}>
        <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 tracking-wider flex items-center justify-between">
          War Configuration
          {isJoinMode && <span className="text-[10px] text-zinc-500 uppercase flex items-center gap-1"><Lock size={10} /> Prescribed by Commander</span>}
        </h3>
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-center">
          {/* RANKED TOGGLE */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={handleToggleRanked}
              className={`relative w-12 h-6 rounded-full transition-colors ${isRanked ? 'bg-tudor-gold' : 'bg-zinc-700'} ${(!userProfile || isJoinMode) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-black transition-transform ${isRanked ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
            <div className="flex flex-col">
              <span className={`text-sm tracking-widest uppercase font-bold ${isRanked ? 'text-tudor-gold' : 'text-zinc-500'}`}>
                {isRanked ? 'Ranked War' : 'Unranked Skirmish'}
              </span>
              {!userProfile?.is_premium && (
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider flex items-center gap-1">
                  <Shield size={10} /> Free Entry
                </span>
              )}
            </div>
          </div>

          {/* BRACKET SELECTOR */}
          <div className="flex gap-2 flex-wrap">
            {Object.values(WAR_BRACKETS).map((b) => {
              const isLocked = !userProfile?.is_premium && (b.id === 'CAMPAIGN' || b.id === 'WAR');
              const isEligibilityLocked = isRanked && userProfile ? userProfile.rating < b.minElo : false;

              return (
                <button
                  key={b.id}
                  onClick={() => setSelectedBracketId(b.id)}
                  disabled={isJoinMode || isEligibilityLocked || isLocked}
                  className={`px-3 py-1 text-xs uppercase tracking-wider border transition-all flex items-center gap-2
                                            ${selectedBracketId === b.id
                      ? 'border-tudor-gold text-tudor-gold bg-tudor-gold/10'
                      : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
                    }
                                            ${(isJoinMode || isEligibilityLocked || isLocked) ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                >
                  {isLocked && <Lock size={10} />}
                  {b.name}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="mb-8">
        {/* WAGER INPUT (Ranked Only) */}
        {isRanked && !isJoinMode && (
          <div className="mb-6">
            <WagerControl
              currentGold={userProfile?.gold || 0}
              wager={wagerAmount}
              onChange={setWagerAmount}
            />
          </div>
        )}

        <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 tracking-wider">Quick Select (Uses Reserves)</h3>
        <StarterPack
          onSelect={handleStarterPack}
          customTemplates={userProfile?.templates}
          maxBudget={WAR_BRACKETS[selectedBracketId].maxCost}
        />
      </div>

      <div className="mb-12">
        <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 tracking-wider">Custom Requisition</h3>
        {/* Pass custom 'maxAvailable' prop if PieceSelector supported it? 
            Currently PieceSelector doesn't know about limits besides generic input.
            We rely on 'handlePieceChange' to enforce limits. 
        */}
        <PieceSelector inventory={inventory} onChange={handlePieceChange} resources={resources} />

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setIsSaveModalOpen(true)}
            disabled={!userProfile || userProfile.id === 'guest_user'}
            className={`flex items-center gap-2 text-xs uppercase tracking-wider transition-colors ${!userProfile || userProfile.id === 'guest_user' ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-tudor-gold'}`}
            title={!userProfile || userProfile.id === 'guest_user' ? "Login required to save templates" : "Save this requisition"}
          >
            <Save size={16} />
            Save Configuration
          </button>
        </div>
      </div>

      {/* Footer / Confirm Area */}
      <div className="fixed bottom-0 left-0 w-full bg-tudor-black border-t border-tudor-gray p-4 flex flex-col md:flex-row gap-4 justify-between items-center z-50 shadow-2xl shadow-black">
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 w-full md:w-auto">
          <div className="flex items-center text-tudor-gold">
            <Shield className="mr-2" size={20} />
            <span className="font-mono text-lg md:text-xl whitespace-nowrap">
              {resources} / {WAR_BRACKETS[selectedBracketId].maxCost} Cap
            </span>
          </div>

          <div className={`flex items-center gap-2 border-l border-tudor-gray/30 pl-4 md:pl-6 ${isJoinMode ? 'opacity-50 grayscale pointer-events-none' : ''}`}>
            <div className="flex bg-black/30 border border-tudor-gray rounded overflow-hidden">
              <button
                onClick={() => setOpponentType('player')}
                className={`px-3 py-1 text-xs uppercase font-mono transition-colors ${opponentType === 'player' ? 'bg-tudor-gold text-black font-bold' : 'text-zinc-400 hover:text-white'}`}
              >
                Player
              </button>
              <button
                onClick={() => setOpponentType('ai')}
                className={`px-3 py-1 text-xs uppercase font-mono transition-colors ${opponentType === 'ai' ? 'bg-tudor-gold text-black font-bold' : 'text-zinc-400 hover:text-white'}`}
              >
                AI
              </button>
            </div>
          </div>

          {/* Difficulty Selector (Only if AI) */}
          {opponentType === 'ai' && (
            <div className={`flex items-center gap-2 border-l border-tudor-gray/30 pl-4 md:pl-6 animate-fade-in ${isJoinMode ? 'opacity-50 grayscale pointer-events-none' : ''}`}>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                className="bg-black/30 border border-tudor-gray text-tudor-gold text-sm px-2 py-1 focus:outline-none focus:border-tudor-gold uppercase font-mono max-w-[100px]"
              >
                <option value="easy">Easy</option>
                <option value="medium">Normal</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          )}
        </div>

        <button
          onClick={handleConfirm}
          className="w-full md:w-auto bg-tudor-gold text-tudor-black font-bold py-3 md:py-2 px-6 rounded hover:bg-yellow-600 transition-colors uppercase tracking-wider text-sm md:text-base"
        >
          {isJoinMode ? "Confirm" : "Mobilize War"}
        </button>
      </div>
    </div>
  );
};

export default Drafting;
